import type { DocElement, ExportDocument, InlineContent, InlineSpan } from './document-model';

const MARKDOWN_ESCAPE_PATTERN = /[\\`*[\]]/g;
const SECTION_SEPARATOR = '\n\n---\n\n';
const BLANK_LINE_PLACEHOLDER = '\u200B';

const escapeMarkdown = (value: string): string => {
  return value.replace(MARKDOWN_ESCAPE_PATTERN, '\\$&');
};

const flattenNewlines = (value: string): string => {
  return value.replace(/\r?\n/g, ' ');
};

const preserveLineBreaks = (value: string): string => {
  if (!value.includes('\n')) {
    return value;
  }

  return value
    .split('\n')
    .map((line) => (line.length > 0 ? line : BLANK_LINE_PLACEHOLDER))
    .join('  \n');
};

const renderSpan = (span: InlineSpan): string => {
  switch (span.type) {
    case 'text':
      return preserveLineBreaks(escapeMarkdown(span.value));
    case 'raw':
      return preserveLineBreaks(span.value);
    case 'bold':
      return `**${flattenNewlines(escapeMarkdown(span.value))}**`;
    case 'italic':
      return `_${flattenNewlines(escapeMarkdown(span.value))}_`;
    case 'code':
      return `\`${span.value}\``;
    case 'link':
      return `[${flattenNewlines(escapeMarkdown(span.label))}](${span.href})`;
  }
};

const renderInline = (content: InlineContent): string => {
  return content.map(renderSpan).join('');
};

const renderElement = (element: DocElement): string => {
  switch (element.type) {
    case 'heading': {
      const prefix = '#'.repeat(element.level);
      return `${prefix} ${renderInline(element.content)}`;
    }
    case 'paragraph':
      return renderInline(element.content);
    case 'list-item': {
      const indent = '  '.repeat(element.indent);
      return `${indent}- ${renderInline(element.content)}`;
    }
    case 'blockquote':
      return renderInline(element.content)
        .split(/\r?\n/)
        .map((line) => `> ${line}`)
        .join('\n');
    case 'image':
      return `![${flattenNewlines(escapeMarkdown(element.alt))}](${element.src})`;
    case 'hr':
      return '---';
  }
};

const renderSectionElements = (elements: DocElement[]): string => {
  const parts: string[] = [];
  let currentGroup: string[] = [];

  for (const element of elements) {
    if (element.type === 'hr') {
      if (currentGroup.length > 0) {
        parts.push(currentGroup.join('\n'));
        currentGroup = [];
      }
    } else {
      currentGroup.push(renderElement(element));
    }
  }
  if (currentGroup.length > 0) {
    parts.push(currentGroup.join('\n'));
  }

  return parts.join(SECTION_SEPARATOR);
};

export const renderDocumentAsMarkdown = (document: ExportDocument): string => {
  const titleHeading = `# ${flattenNewlines(escapeMarkdown(document.title))}`;

  const sectionTexts = document.sections
    .filter((section) => section.elements.length > 0)
    .map((section) => renderSectionElements(section.elements));

  return [titleHeading, ...sectionTexts].join(SECTION_SEPARATOR);
};
